import { NotesRounded } from "@mui/icons-material";
import { Button, Grid, Paper, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SINGLE_CLINIC_USER } from "../../../../../enums/UrlPaths";
import useUser from "../../../../../hooks/useUser";
import HeaderCard from "./HeaderCard";
import { CARD_RADIUS, CardTitle } from "./LinkCard";
import OwnerCard from "./OwnerCard";
import PetCard from "./PetCard";
import { getAppointmentOwner } from "../../utils/getAppointmentOwner";

const DESCRIPTION_PREVIEW_LENGTH = 500;

/** Appointment header with its status, service, place and time, the pet and owner cards, and the description. */
export default function AppointmentBanner({ appointment, actions }) {
  const { roles } = useUser();
  const owner = getOwner(appointment, roles);
  const hasPet = Boolean(appointment?.pet);
  const hasSideColumn = hasPet || Boolean(owner);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={hasSideColumn ? 6 : 12}>
        <HeaderCard appointment={appointment} actions={actions} />
      </Grid>
      {hasSideColumn ? (
        <Grid item xs={12} md={6}>
          <Stack spacing={2} height={"100%"}>
            {hasPet ? <PetCard appointment={appointment} /> : null}
            {owner ? <OwnerCard owner={owner} /> : null}
          </Stack>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <DescriptionCard description={appointment?.description} />
      </Grid>
    </Grid>
  );
}

/** Shows the first 500 characters of a longer description, with a toggle to show the rest. */
function DescriptionCard({ description }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isLong = description?.length > DESCRIPTION_PREVIEW_LENGTH;
  const visibleText =
    isLong && !expanded
      ? description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd() + "…"
      : description;

  return (
    <Paper sx={{ p: 2, borderRadius: CARD_RADIUS }}>
      <CardTitle icon={<NotesRounded />} title={t("Description")} />
      <Typography
        variant="body2"
        mt={1}
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        data-testid="appointment.details.field-Description"
      >
        {visibleText || t("NoInfo")}
      </Typography>
      {isLong ? (
        <Button
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ mt: 0.5, px: 0, textTransform: "none" }}
          data-testid="appointment.details.description-toggle"
        >
          {expanded ? t("ShowLess") : t("ShowMore")}
        </Button>
      ) : null}
    </Paper>
  );
}

function getOwner(appointment, roles) {
  if (roles.user || !appointment) {
    return null;
  }

  const owner = getAppointmentOwner(appointment);
  if (owner) {
    const name = [owner.userDetails?.firstName, owner.userDetails?.lastName]
      .filter(Boolean)
      .join(" ");
    return { name, to: SINGLE_CLINIC_USER + owner.id };
  }

  if (appointment.user?.clinic?.id == null) {
    return { name: null, to: null, deleted: true };
  }

  return null;
}
